import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Lock, RefreshCw, CheckCircle2, UserCheck, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

function ItemState({ i, ck, checked, error }) {
  const ex = ck.item_expiry?.[i.key];
  const fmt = ck.format_errors?.[i.key];
  const saved = !!ck.checks?.[i.key];
  return (
    <div className="text-xs mt-1 space-y-0.5" data-testid={`check-state-${i.key}`}>
      {checked && ck.auto?.[i.key] && <div className="text-emerald-700 font-medium" data-testid={`auto-verified-${i.key}`}><CheckCircle2 className="w-3 h-3 inline mr-1" />Auto-verified from upload</div>}
      {checked && saved && !ck.auto?.[i.key] && <div className="text-sky-700" data-testid={`manual-tick-${i.key}`}><UserCheck className="w-3 h-3 inline mr-1" />Manually ticked</div>}
      {ex?.status === "expired" && <div className="text-rose-700 font-medium" data-testid={`expired-${i.key}`}>Expired — {ex.doc} expired on {ex.expiry}</div>}
      {ex?.status === "expiring" && <div className="text-amber-700" data-testid={`expiring-${i.key}`}><AlertTriangle className="w-3 h-3 inline mr-1" />Expiring Soon — {ex.doc} expires {ex.expiry}</div>}
      {fmt && !checked && <div className="text-rose-600" data-testid={`format-warn-${i.key}`}>{fmt}</div>}
      {error && <div className="text-rose-700 font-semibold" data-testid={`check-error-${i.key}`}>{error}</div>}
    </div>
  );
}

const ROW_TONE = (ck, key, checked, error) => {
  const s = ck.item_expiry?.[key]?.status;
  if (s === "expired" || error) return "bg-rose-50";
  if (s === "expiring") return "bg-amber-50";
  return checked ? "bg-emerald-50/60" : "";
};

function Group({ g, ck, errors, checks, remarks, setChecks, setRemarks, editable, lockedMsg }) {
  const done = g.items.filter((i) => checks[i.key]).length;
  const disabled = !editable || !!lockedMsg;
  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${editable ? "border-cyan-300" : "border-slate-200"}`} data-testid={`checklist-group-${g.id}`}>
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 space-y-2">
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          <span className="font-semibold text-slate-800">Group {g.id} — {g.name} <span className="font-normal text-slate-400">({g.ownerLabel}{editable ? ", yours" : ", read-only"})</span></span>
          <span className="font-mono text-slate-600" data-testid={`group-progress-${g.id}`}>{done}/{g.items.length} Completed</span>
        </div>
        <Progress value={(done / g.items.length) * 100} className="h-1.5" />
        {lockedMsg && <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="g10-locked"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />{lockedMsg}</div>}
      </div>
      <div className="divide-y divide-slate-100">
        {g.items.map((i) => {
          const expired = ck.item_expiry?.[i.key]?.status === "expired";
          return (
          <div key={i.key} className={`grid grid-cols-1 sm:grid-cols-[320px_1fr] gap-2 sm:gap-4 items-start px-4 py-2.5 ${ROW_TONE(ck, i.key, checks[i.key], errors[i.key])}`} data-testid={`check-row-${i.key}`}>
            <div>
              <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={!!checks[i.key]} disabled={disabled || (expired && !checks[i.key])} onCheckedChange={(v) => setChecks({ ...checks, [i.key]: !!v })} data-testid={`check-${i.key}`}
                  className={ck.auto?.[i.key] && checks[i.key] ? "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" : ""} />
                {i.label}{ck.critical?.includes(i.key) && <span className="text-[10px] font-semibold text-rose-600 uppercase">Critical</span>}
              </label>
              <ItemState i={i} ck={ck} checked={!!checks[i.key]} error={errors[i.key]} />
            </div>
            <Input value={remarks[i.key] || ""} disabled={disabled} placeholder="Remarks" onChange={(e) => setRemarks({ ...remarks, [i.key]: e.target.value })} className="h-8 text-sm bg-white" data-testid={`remark-${i.key}`} />
          </div>
          );
        })}
      </div>
    </div>
  );
}

const OWNER_LABEL = { sourcing: "Identity", docs: "Docs", supdt: "Superintendent", office_admin: "Office Admin", travel: "Travel", management: "Management" };

export function GroupTotals({ groups, testid }) {
  const by = {};
  (groups || []).forEach((g) => { const o = (by[g.owner_key] ||= { done: 0, total: 0 }); o.done += g.done; o.total += g.total; });
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {Object.entries(by).map(([k, v]) => (
        <span key={k} className={`text-[11px] px-1.5 py-0.5 rounded border font-mono ${v.done === v.total ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-600"}`} data-testid={`${testid}-${k}`}>
          {OWNER_LABEL[k]}: {v.done}/{v.total}
        </span>
      ))}
    </div>
  );
}

function OverrideDialog({ open, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="override-dialog">
        <DialogHeader><DialogTitle>Override sign-off lock</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Groups 1–9 are not complete. A reason is required and will be recorded in the candidate's audit log.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for override (required)" data-testid="override-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="override-cancel-btn">Cancel</Button>
          <Button disabled={busy || reason.trim().length < 5} onClick={() => onSubmit(reason)} className="bg-rose-700 hover:bg-rose-800" data-testid="override-confirm-btn">Unlock sign-off</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CandidateChecklist({ candidate, meta, onChanged }) {
  const caps = meta.caps;
  const [checks, setChecks] = useState(candidate.checklist.checks);
  const [remarks, setRemarks] = useState(candidate.checklist.remarks || {});
  const [busy, setBusy] = useState(false);
  const [ovOpen, setOvOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [note, setNote] = useState(candidate.checklist.note || "");
  const ck = candidate.checklist;
  const ov = candidate.checklist.override;
  useEffect(() => { setChecks(candidate.checklist.checks); setRemarks(candidate.checklist.remarks || {}); setErrors({}); }, [candidate]);

  const owners = Object.fromEntries(candidate.groups.map((g) => [g.id, g.owner]));
  const groups = meta.groups.filter((g) => caps.view_groups.includes(g.id)).map((g) => ({ ...g, ownerLabel: owners[g.id] }));
  const savedDone = (id) => meta.groups.find((g) => g.id === id).items.every((i) => candidate.checklist.checks[i.key]);
  const pendingBefore10 = meta.groups.filter((g) => g.id < 10 && !savedDone(g.id)).map((g) => g.name);
  const lock10 = pendingBefore10.length && !ov ? `Locked until Groups 1–9 are complete. Still incomplete: ${pendingBefore10.join(", ")}.` : null;
  const docsReady = [1, 2, 3, 4, 5, 6].every(savedDone);
  const inQueue = ["Sourcing", "Docs Verification"].includes(candidate.current_stage);
  const total = Object.keys(checks).length;
  const done = Object.values(checks).filter(Boolean).length;
  const exp = Object.values(ck.item_expiry || {});
  const uniq = (s) => new Set(exp.filter((x) => x.status === s).map((x) => x.doc)).size;
  const nExpiring = uniq("expiring");
  const nExpired = Math.max(uniq("expired"), ck.expired_docs || 0);
  const criticalMissing = (ck.critical || []).filter((k) => !candidate.checklist.checks[k]);
  const labelOf = (k) => meta.groups.flatMap((g) => g.items).find((i) => i.key === k)?.label;
  const verifyBlock = ck.expired_docs ? `Cannot verify — ${ck.expired_docs} doc(s) expired. Check Expiry Board.`
    : criticalMissing.length ? "Critical checks incomplete — cannot move to Shortlisted." : !docsReady ? "Groups 1–6 must be complete and saved" : "";
  const needsNote = caps.docs && meta.groups.some((g) => caps.edit_groups.includes(g.id) && g.items.some((i) => !checks[i.key]));

  const save = async () => {
    setBusy(true);
    setErrors({});
    try {
      await api.put(`/candidates/${candidate.id}/checklist`, { checks, remarks, note });
      toast.success("Checklist saved");
      onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.errors) { setErrors(d.errors); toast.error(d.message); } else toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="candidate-checklist">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 sticky top-16 z-10">
        <div className="flex-1 min-w-[220px] space-y-1.5">
          <div className="text-sm font-semibold text-slate-800" data-testid="checklist-progress-text">
            <span className="text-emerald-700">{done}/{total} Verified</span> <span className="text-slate-300">|</span> <span className="text-amber-700" data-testid="checklist-expiring-count">{nExpiring} Expiring Soon</span> <span className="text-slate-300">|</span> <span className="text-rose-700" data-testid="checklist-expired-count">{nExpired} Expired</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-slate-100" data-testid="checklist-progress-bar">
            <div className="bg-emerald-500" style={{ width: `${(done / total) * 100}%` }} />
            <div className="bg-amber-400" style={{ width: `${(nExpiring / total) * 100}%` }} />
            <div className="bg-rose-500" style={{ width: `${(nExpired / total) * 100}%` }} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {caps.can_checklist && <Button variant="outline" onClick={() => run(() => api.post(`/candidates/${candidate.id}/checklist/recheck`), "Rechecked from documents")} disabled={busy} data-testid="recheck-docs-btn"><RefreshCw className="w-4 h-4 mr-1" /> Recheck from Documents</Button>}
          {caps.can_checklist && <Button variant="outline" onClick={save} disabled={busy} data-testid="checklist-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>}
          {(caps.docs || caps.is_super) && inQueue && (
            <span title={verifyBlock} data-testid="mark-verified-wrap">
            <Button onClick={() => run(() => api.post(`/candidates/${candidate.id}/mark-verified`), "Candidate verified and shortlisted")} disabled={busy || !!verifyBlock}
              className="bg-emerald-700 hover:bg-emerald-800" data-testid="mark-verified-btn" title={verifyBlock}>
              <ShieldCheck className="w-4 h-4 mr-1" /> Mark as Verified
            </Button>
            </span>
          )}
        </div>
      </div>
      {criticalMissing.length > 0 && <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 font-medium" data-testid="critical-banner">Critical checks incomplete — cannot move to Shortlisted. <span className="font-normal">Missing: {criticalMissing.map(labelOf).join(", ")}</span></div>}
      {ck.expired_docs > 0 && <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="expired-docs-banner">Cannot verify — {ck.expired_docs} doc(s) expired. Check Expiry Board.</div>}
      {needsNote && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-1.5" data-testid="checklist-note">
          <div className="text-sm font-medium text-slate-800">Save remarks <span className="text-rose-600">*</span></div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why is the checklist still incomplete?" className={errors.note ? "border-rose-400" : ""} data-testid="checklist-note-input" />
          {errors.note ? <div className="text-xs text-rose-700 font-semibold" data-testid="checklist-note-error">{errors.note}</div>
            : <div className="text-xs text-slate-500">Checklist incomplete — remarks required before saving.</div>}
        </div>
      )}
      {caps.is_sourcing && !caps.full && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="sourcing-group-summary">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Checklist progress by team (read-only)</div>
          <GroupTotals groups={candidate.groups} testid="profile-group-totals" />
        </div>
      )}
      {ov && <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="override-banner">Sign-off lock overridden by {ov.by_name} on {new Date(ov.at).toLocaleString()} — {ov.reason}</div>}
      {lock10 && caps.is_super && (
        <Button variant="outline" className="text-rose-700 border-rose-300" onClick={() => setOvOpen(true)} data-testid="override-signoff-btn"><Lock className="w-4 h-4 mr-1" /> Override sign-off lock</Button>
      )}
      <OverrideDialog open={ovOpen} busy={busy} onClose={() => setOvOpen(false)}
        onSubmit={(reason) => run(() => api.post(`/candidates/${candidate.id}/signoff-override`, { reason }), "Sign-off unlocked").then(() => setOvOpen(false))} />
      {groups.length === 0 && <div className="text-sm text-slate-500" data-testid="checklist-no-groups">Your role has no checklist group for this candidate.</div>}
      {groups.map((g) => (
        <Group key={g.id} g={g} ck={ck} errors={errors} checks={checks} remarks={remarks} setChecks={setChecks} setRemarks={setRemarks}
          editable={caps.edit_groups.includes(g.id)} lockedMsg={g.id === 10 ? lock10 : null} />
      ))}
    </div>
  );
}
